// Layer 4 (UI) — the condition trend on a time axis (polish.P21; the locked
// equipment-beat Condition A chart as the Maintenance comp A draws it): the
// measured line broken across each missing-sample run, the run labelled by the
// stoppage the downtime log holds, the Reading's warn and limit lines, the
// current run above the warn line tinted, and — when the point projects to its
// limit — the cursor, the dotted projection to the limit instant and the case's
// proposed stop on the forward half.
//
// Presentational: every mark is a field of `ConditionPlot`; the component only
// maps times and values to pixels at its own measured width.

import { useLayoutEffect, useRef, useState } from 'react';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { diffMinutes } from '@/versions/v1/shared/clock.derive';
import { vizToneColor, type TrendReference } from '@/versions/v1/modules/_viz';
import type { ConditionPlot } from './ConditionDetail.transform';

const PAD = { l: 34, r: 10, t: 12, b: 22 } as const;
const TICK_PX = 10;
/** About how wide one character of a 10px label runs, for edge placement only. */
const CHAR_W = 5.2;
/** A day label is about this wide at the tick size; closer labels thin. */
const DAY_LABEL_MIN_GAP = 42;

export function ConditionTrendChart({
  plot,
  references,
  height,
  label,
  alert,
}: {
  plot: ConditionPlot;
  references: readonly TrendReference[];
  height: number;
  label: string;
  /** The line's latest point is the abnormal reading. */
  alert: boolean;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(280, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerW = width - PAD.l - PAD.r;
  const innerH = height - PAD.t - PAD.b;
  const span = diffMinutes(plot.window.endAt, plot.window.startAt);
  const x = (at: SeriesTime) => PAD.l + (span <= 0 ? 0 : innerW * (diffMinutes(at, plot.window.startAt) / span));
  const y = (value: number) =>
    PAD.t + innerH * ((plot.yDomain.max - value) / (plot.yDomain.max - plot.yDomain.min));
  const bottom = PAD.t + innerH;

  let line = '';
  let penDown = false;
  for (const point of plot.points) {
    if (point.value === null) {
      penDown = false;
      continue;
    }
    line += `${penDown ? 'L' : 'M'}${x(point.at).toFixed(1)} ${y(point.value).toFixed(1)}`;
    penDown = true;
  }

  const above = plot.aboveBand;
  const band = plot.bandHigh;
  const aboveArea = band !== null && above.length > 1
    ? `M${x(above[0]!.at).toFixed(1)} ${y(band).toFixed(1)}${above
        .map((point) => `L${x(point.at).toFixed(1)} ${y(Math.max(band, point.value)).toFixed(1)}`)
        .join('')}L${x(above.at(-1)!.at).toFixed(1)} ${y(band).toFixed(1)}Z`
    : null;

  // Day labels thin to every second (third …) day when the axis is narrow.
  const dayGap = plot.dayTicks.length > 1 ? x(plot.dayTicks[1]!.at) - x(plot.dayTicks[0]!.at) : innerW;
  const stride = Math.max(1, Math.ceil(DAY_LABEL_MIN_GAP / Math.max(dayGap, 1)));

  // The limit label sits right of its dot; near the plot's edge it end-aligns
  // to the edge instead, so it never crosses back over the proposed stop.
  const projection = plot.projection;
  const projectionLabelW = projection ? projection.label.length * CHAR_W : 0;
  const projectionLabelRight = projection ? x(projection.toAt) + 6 + projectionLabelW <= width - PAD.r : true;

  return (
    <div ref={wrapperRef} data-condition-trend>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="block"
        role="graphics-document"
        aria-label={label}
      >
        {plot.yTicks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(tick)} y2={y(tick)} stroke="var(--hairline-strong)" />
            <text x={PAD.l - 6} y={y(tick) + 3.5} fontSize={TICK_PX} textAnchor="end" fill="var(--ink-tertiary)">
              {tick}
            </text>
          </g>
        ))}
        {plot.dayTicks.map((tick, index) => {
          const tx = x(tick.at);
          if (index % stride !== 0 || tx > width - PAD.r - 14) return null;
          return (
            <text key={tick.at} data-condition-day-tick={tick.label} x={tx} y={height - 5} fontSize={TICK_PX} textAnchor="middle" fill="var(--ink-tertiary)">
              {tick.label}
            </text>
          );
        })}

        {plot.gaps.map((gap) => (
          <g key={gap.startAt}>
            <rect
              data-trend-gap
              data-gap-start={gap.startAt}
              data-gap-end={gap.endAt}
              x={x(gap.startAt)}
              y={PAD.t}
              width={Math.max(2, x(gap.endAt) - x(gap.startAt))}
              height={innerH}
              fill="var(--ink-tertiary)"
              opacity={0.16}
            />
            {gap.label ? (
              <text x={x(gap.endAt) + 5} y={PAD.t + 10} fontSize={TICK_PX} fill="var(--ink-secondary)" data-trend-gap-label>
                {gap.label}
              </text>
            ) : null}
          </g>
        ))}

        {plot.now ? (
          <rect x={x(plot.now.at)} y={PAD.t} width={Math.max(0, x(plot.window.endAt) - x(plot.now.at))} height={innerH} fill="var(--ink-tertiary)" opacity={0.05} />
        ) : null}

        {plot.stop ? (
          <g data-condition-stop={plot.stop.startAt}>
            <rect
              x={x(plot.stop.startAt)}
              y={PAD.t}
              width={Math.max(4, x(plot.stop.endAt) - x(plot.stop.startAt))}
              height={innerH}
              fill="var(--ink-primary)"
              opacity={0.55}
            />
            <text x={x(plot.stop.startAt) - 6} y={bottom - 16} fontSize={TICK_PX} textAnchor="end" fill="var(--ink-primary)" fontWeight={600}>
              {plot.stop.label}
            </text>
          </g>
        ) : null}

        {references.map((ref) => (
          <g key={ref.label}>
            <line
              data-condition-reference={ref.value}
              x1={PAD.l}
              x2={width - PAD.r}
              y1={y(ref.value)}
              y2={y(ref.value)}
              stroke={vizToneColor(ref.tone)}
              strokeWidth={1.2}
              strokeDasharray="4 3"
            />
            <text x={PAD.l + 4} y={y(ref.value) + 13} fontSize={TICK_PX} fill={vizToneColor(ref.tone)}>
              {ref.label}
            </text>
          </g>
        ))}

        {aboveArea ? <path d={aboveArea} fill="var(--state-blocked)" opacity={0.16} data-condition-above-band={above[0]!.at} /> : null}
        <path d={line} fill="none" stroke="var(--ink-primary)" strokeWidth={1.5} strokeLinejoin="round" />

        {projection ? (
          <g data-condition-projection={projection.toAt}>
            <line
              x1={x(projection.fromAt)}
              y1={y(projection.fromValue)}
              x2={x(projection.toAt)}
              y2={y(projection.toValue)}
              stroke="var(--state-blocked)"
              strokeWidth={1.6}
              strokeDasharray="2 3"
            />
            <circle cx={x(projection.toAt)} cy={y(projection.toValue)} r={3.5} fill="var(--state-blocked)" />
            <text
              x={projectionLabelRight ? x(projection.toAt) + 6 : width - PAD.r}
              y={y(projection.toValue) - 8}
              fontSize={TICK_PX}
              textAnchor={projectionLabelRight ? 'start' : 'end'}
              fill="var(--state-blocked)"
              fontWeight={600}
            >
              {projection.label}
            </text>
          </g>
        ) : null}

        {plot.now ? (
          <g data-condition-now={plot.now.at}>
            <line x1={x(plot.now.at)} x2={x(plot.now.at)} y1={PAD.t} y2={bottom} stroke="var(--color-accent-500)" strokeWidth={1.2} />
            <text x={x(plot.now.at) + 5} y={PAD.t + 10} fontSize={TICK_PX} fill="var(--color-accent-500)" fontWeight={600}>
              {plot.now.label}
            </text>
          </g>
        ) : null}

        {plot.latest ? (
          <circle
            cx={x(plot.latest.at)}
            cy={y(plot.latest.value)}
            r={3.2}
            fill={alert ? 'var(--state-blocked)' : 'var(--ink-primary)'}
            stroke="var(--surface-2)"
            strokeWidth={1.2}
          />
        ) : null}
      </svg>
    </div>
  );
}
